import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, Skeleton, StatusBadge, Switch, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action, upload } from '../lib/api.ts';
import { dateTime, day } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { downloadCsv, Kpi, Table } from './Reports.tsx';

/**
 * The document protocol register: every document that comes in or goes out gets the next number
 * of the year. Incoming ones are given to someone to handle by a date and are done when answered
 * or closed; everything that happens to an entry stays in its history.
 */

type Direction = 'in' | 'out' | 'internal';
type Channel = 'post' | 'hand' | 'email' | 'portal' | 'other';
type State = 'registered' | 'assigned' | 'answered' | 'closed' | 'void';
type View = 'open' | 'overdue' | 'mine' | 'done' | 'all';
interface Row {
  id: string;
  name: string;
  number: number;
  year: number;
  direction: Direction;
  date: string;
  subject: string | null;
  party: string | null;
  channel: Channel;
  their_ref: string | null;
  folder: string | null;
  confidential: boolean;
  hidden: boolean;
  assignee: string | null;
  assignee_name: string | null;
  due: string | null;
  overdue: boolean;
  state: State;
  files: number;
  reply_to: string | null;
}
interface Summary {
  open: number;
  overdue: number;
  unassigned: number;
  mine: number;
  mine_overdue: number;
  registered_today: number;
  years: number[];
  next: string;
  can: { register: boolean; manage: boolean; handle: boolean };
}
interface Detail {
  entry: Omit<Row, 'subject' | 'party' | 'hidden' | 'files'> & {
    subject: string;
    party: string;
    their_date: string | null;
    pages: number | null;
    outcome: string | null;
    created_at: string;
    registered_by_name: string | null;
  };
  history: { id: string; kind: string; text: string | null; data: Record<string, any> | null; at: string; by_name: string | null }[];
  files: { id: string; name: string; mime: string; size: number; at: string; by_name: string | null }[];
  replies: Row[];
  answers: Row | null;
  can: { manage: boolean; handle: boolean; attach: boolean; reply: boolean };
}
type Person = { id: string; name: string };

const DIRS: { key: Direction; icon: IconName }[] = [
  { key: 'in', icon: 'download' },
  { key: 'out', icon: 'send' },
  { key: 'internal', icon: 'transfer' },
];
const DIR_ICON = Object.fromEntries(DIRS.map((d) => [d.key, d.icon])) as Record<Direction, IconName>;
const CHANNELS: Channel[] = ['post', 'hand', 'email', 'portal', 'other'];
const VIEWS: View[] = ['open', 'overdue', 'mine', 'done', 'all'];
const STATE: Record<State, { tone: Tone; icon: IconName }> = {
  registered: { tone: 'warn', icon: 'bell' },
  assigned: { tone: 'info', icon: 'users' },
  answered: { tone: 'ok', icon: 'send' },
  closed: { tone: 'ok', icon: 'check' },
  void: { tone: 'neutral', icon: 'x' },
};
const MAX_FILE = 8_000_000;
const ACCEPT = 'application/pdf,image/jpeg,image/png';

const stateLabel = (s: State) => t(`prot.st_${s}` as MessageKey);
const subjectOf = (r: { hidden?: boolean; subject: string | null }) => (r.hidden ? t('prot.confidential_entry') : r.subject ?? '');

function StateBadge({ r }: { r: { state: State; overdue: boolean } }) {
  if (r.overdue) return <StatusBadge tone="danger" icon="alert" label={t('prot.late')} />;
  const s = STATE[r.state];
  return <StatusBadge tone={s.tone} icon={s.icon} label={stateLabel(r.state)} />;
}

export function Protocol() {
  const [params, setParams] = useSearchParams();
  const view = (VIEWS.includes(params.get('view') as View) ? params.get('view') : 'open') as View;
  const direction = (['in', 'out', 'internal'].includes(params.get('dir') ?? '') ? params.get('dir') : null) as Direction | null;
  const [q, setQ] = useState(params.get('q') ?? '');
  const [query, setQuery] = useState(q);
  const { data: sum, reload: reloadSum } = useAction<Summary>('protocol.summary');
  const year = Number(params.get('year')) || null;
  const { data: rows, reload } = useAction<Row[]>('protocol.list', { view: query ? 'all' : view, direction, q: query || null, year });
  const [open, setOpen] = useState<string | null>(params.get('entry'));
  const [registering, setRegistering] = useState<null | { reply?: Detail['entry'] }>(null);
  const [book, setBook] = useState(false);
  useEffect(() => {
    const h = setTimeout(() => setQuery(q.trim()), 250);
    return () => clearTimeout(h);
  }, [q]);
  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) (v === null ? p.delete(k) : p.set(k, v));
    setParams(p, { replace: true });
  };
  const changed = async () => {
    await Promise.all([reload(), reloadSum()]);
  };
  const show = (id: string | null) => {
    setOpen(id);
    set({ entry: id });
  };
  const count = (v: View) => (v === 'open' ? sum?.open : v === 'overdue' ? sum?.overdue : v === 'mine' ? sum?.mine : undefined);

  return (
    <Shell>
      <div className="page reports protocol">
        <div className="page-head">
          <div>
            <h1>{t('prot.title')}</h1>
            <p className="muted">{t('prot.subtitle')}</p>
          </div>
          <div className="page-actions">
            <Button icon="book" onClick={() => setBook(true)}>
              {t('prot.book')}
            </Button>
            {sum?.can.register ? (
              <Button variant="primary" icon="plus" onClick={() => setRegistering({})}>
                {t('prot.register')}
              </Button>
            ) : null}
          </div>
        </div>
        {!sum ? (
          <Skeleton h={110} r="var(--radius-lg)" />
        ) : (
          <section className="rep-kpis" aria-label={t('prot.at_a_glance')}>
            <Kpi label={t('prot.k_open')} value={String(sum.open)} hint={tn('prot.k_unassigned', sum.unassigned)} />
            <Kpi label={t('prot.k_late')} value={String(sum.overdue)} hint={sum.overdue ? t('prot.k_late_hint') : t('prot.k_none_late')} warn={sum.overdue > 0} />
            <Kpi label={t('prot.k_mine')} value={String(sum.mine)} hint={sum.mine_overdue ? tn('prot.k_mine_late', sum.mine_overdue) : t('prot.k_mine_hint')} warn={sum.mine_overdue > 0} />
            <Kpi label={t('prot.k_today')} value={String(sum.registered_today)} hint={t('prot.k_next', { name: sum.next })} />
          </section>
        )}

        <div className="toolbar prot-toolbar">
          <div className="segmented tabs" role="tablist" aria-label={t('prot.show')}>
            {VIEWS.map((v) => (
              <button key={v} role="tab" aria-selected={!query && view === v} className={!query && view === v ? 'active' : ''} onClick={() => (setQ(''), set({ view: v === 'open' ? null : v }))}>
                {t(`prot.v_${v}` as MessageKey)}
                {count(v) ? <span className="count">{count(v)}</span> : null}
              </button>
            ))}
          </div>
          <span className="row-actions prot-filters">
            <label className="search">
              <Icon name="search" size={16} />
              <input className="input" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('prot.search')} aria-label={t('prot.search')} />
            </label>
            <select className="input" value={direction ?? ''} onChange={(e) => set({ dir: e.target.value || null })} aria-label={t('prot.direction')}>
              <option value="">{t('prot.all_directions')}</option>
              {DIRS.map((d) => (
                <option key={d.key} value={d.key}>
                  {t(`prot.dir_${d.key}` as MessageKey)}
                </option>
              ))}
            </select>
            {sum && sum.years.length > 1 ? (
              <select className="input" value={year ?? ''} onChange={(e) => set({ year: e.target.value || null })} aria-label={t('prot.year')}>
                <option value="">{t('prot.all_years')}</option>
                {sum.years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            ) : null}
          </span>
        </div>

        {!rows ? (
          <Skeleton h={300} r="var(--radius-lg)" />
        ) : !rows.length ? (
          query ? (
            <EmptyState icon="search" title={t('prot.none_found')} body={t('prot.none_found_body', { q: query })} />
          ) : view === 'all' && !direction ? (
            <EmptyState
              icon="book"
              title={t('prot.empty')}
              body={t('prot.empty_body')}
              action={
                sum?.can.register ? (
                  <Button variant="primary" icon="plus" onClick={() => setRegistering({})}>
                    {t('prot.register')}
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <EmptyState icon="checkCircle" title={t(`prot.empty_${view}` as MessageKey)} />
          )
        ) : (
          <Table
            view="protocol.register"
            head={[t('prot.c_no'), t('prot.c_subject'), t('prot.c_date'), t('prot.c_handler'), t('prot.c_due'), t('prot.c_state')]}
            phoneHide={[2, 3, 4]}
            left={[0, 1, 3]}
            rows={rows.map((r) => ({
              key: r.id,
              onClick: () => show(r.id),
              cells: [
                <span className="prot-no">
                  <Icon name={DIR_ICON[r.direction]} size={16} />
                  <span className="tabular strong">{r.name}</span>
                </span>,
                <span className="vat-party prot-subject">
                  <span className={r.hidden ? 'muted' : ''}>
                    {r.confidential ? <Icon name="lock" size={14} /> : null} {subjectOf(r)}
                  </span>
                  <span className="muted">
                    {[r.party ? t(r.direction === 'out' ? 'prot.to_x' : 'prot.from_x', { name: r.party }) : null, r.folder, r.files ? tn('prot.n_files', r.files) : null].filter(Boolean).join(' · ') || ' '}
                  </span>
                </span>,
                <span className="tabular">{day(r.date)}</span>,
                <span>{r.assignee_name ?? (r.direction === 'in' && (r.state === 'registered' || r.state === 'assigned') ? <span className="muted">{t('prot.nobody_yet')}</span> : '—')}</span>,
                <span className={`tabular${r.overdue ? ' text-danger strong' : ''}`}>{r.due ? day(r.due) : '—'}</span>,
                <StateBadge r={r} />,
              ],
            }))}
          />
        )}
      </div>
      {open ? (
        <EntryDialog
          id={open}
          onClose={() => show(null)}
          onChange={changed}
          onOpen={show}
          onReply={(entry) => setRegistering({ reply: entry })}
        />
      ) : null}
      {registering ? (
        <RegisterDialog
          reply={registering.reply}
          manage={!!sum?.can.manage}
          onClose={() => setRegistering(null)}
          onDone={async (id) => {
            setRegistering(null);
            await changed();
            show(id);
          }}
        />
      ) : null}
      {book && sum ? <BookDialog years={sum.years} onClose={() => setBook(false)} /> : null}
    </Shell>
  );
}

/* ---------------------------------------------------------------- One entry */

function EntryDialog({ id, onClose, onChange, onOpen, onReply }: { id: string; onClose: () => void; onChange: () => Promise<void>; onOpen: (id: string) => void; onReply: (e: Detail['entry']) => void }) {
  const { data, reload } = useAction<Detail>('protocol.get', { id });
  const [ask, setAsk] = useState<null | 'assign' | 'note' | 'close' | 'reopen' | 'correct' | 'void'>(null);
  const done = async () => {
    setAsk(null);
    await Promise.all([reload(), onChange()]);
  };
  if (!data) {
    return (
      <Dialog title={t('prot.entry')} onClose={onClose} size="lg">
        <Skeleton h={260} />
      </Dialog>
    );
  }
  const e = data.entry;
  const openState = e.state === 'registered' || e.state === 'assigned';
  const can = data.can;
  const fact = (label: string, value: React.ReactNode) =>
    value ? (
      <>
        <dt>{label}</dt>
        <dd>{value}</dd>
      </>
    ) : null;

  return (
    <Dialog
      title={`${t('prot.no_x', { name: e.name })} · ${e.subject}`}
      onClose={onClose}
      size="lg"
      footer={
        e.state === 'void' ? undefined : (
          <>
            <span className="foot-actions">
              {can.manage ? (
                <Button variant="ghost" icon="edit" onClick={() => setAsk('correct')}>
                  {t('prot.correct')}
                </Button>
              ) : null}
              {can.manage && !data.replies.some((r) => r.state !== 'void') ? (
                <Button variant="ghost" icon="x" onClick={() => setAsk('void')}>
                  {t('prot.void')}
                </Button>
              ) : null}
              {can.manage && e.direction === 'in' && !openState ? (
                <Button variant="ghost" icon="undo" onClick={() => setAsk('reopen')}>
                  {t('prot.reopen')}
                </Button>
              ) : null}
            </span>
            {openState && can.handle ? (
              <Button icon="check" onClick={() => setAsk('close')}>
                {t('prot.close')}
              </Button>
            ) : null}
            {openState && can.reply ? (
              <Button variant="primary" icon="send" onClick={() => onReply(e)}>
                {t('prot.answer')}
              </Button>
            ) : null}
          </>
        )
      }
    >
      <div className="form-stack">
        <div className="item-summary">
          <div>
            <span className="muted">{t('prot.c_no')}</span>
            <strong className="tabular">{e.name}</strong>
          </div>
          <div>
            <span className="muted">{t('prot.c_date')}</span>
            <strong>{day(e.date)}</strong>
          </div>
          <div>
            <span className="muted">{t('prot.direction')}</span>
            <strong>
              <Icon name={DIR_ICON[e.direction]} size={14} /> {t(`prot.dir_${e.direction}` as MessageKey)}
            </strong>
          </div>
          <StateBadge r={e} />
        </div>
        {e.confidential ? (
          <p className="callout">
            <Icon name="lock" size={16} />
            <span>{t('prot.confidential_note')}</span>
          </p>
        ) : null}
        {e.state === 'void' ? (
          <p className="callout callout-warn">
            <Icon name="x" size={16} />
            <span>{t('prot.voided_because', { reason: e.outcome ?? '' })}</span>
          </p>
        ) : null}

        {e.direction === 'in' && e.state !== 'void' ? (
          <div className="prot-handler">
            <Icon name="users" size={18} />
            <span>
              {e.assignee_name ? (
                <>
                  <span className="muted">{t('prot.handled_by')} </span>
                  <strong>{e.assignee_name}</strong>
                  {e.due ? <span className={e.overdue ? 'text-danger strong' : 'muted'}> · {t('prot.answer_by', { day: day(e.due) })}</span> : null}
                </>
              ) : (
                <span className="muted">{t('prot.not_given')}</span>
              )}
            </span>
            {can.manage && openState ? (
              <Button size="sm" icon="users" onClick={() => setAsk('assign')}>
                {e.assignee ? t('prot.give_other') : t('prot.give')}
              </Button>
            ) : null}
          </div>
        ) : null}

        <dl className="appr-facts">
          {fact(e.direction === 'out' ? t('prot.to') : t('prot.from'), e.party)}
          {fact(e.direction === 'out' ? t('prot.sent_by') : t('prot.channel'), t(`prot.ch_${e.channel}` as MessageKey))}
          {fact(t('prot.their_ref'), [e.their_ref, e.their_date ? day(e.their_date) : null].filter(Boolean).join(' · '))}
          {fact(t('prot.pages'), e.pages ? String(e.pages) : null)}
          {fact(t('prot.folder'), e.folder)}
          {fact(t('prot.registered_by'), [e.registered_by_name, dateTime(e.created_at)].filter(Boolean).join(' · '))}
          {fact(t('prot.outcome'), e.state === 'closed' && e.direction === 'in' ? e.outcome : null)}
        </dl>

        {data.answers ? (
          <section className="item-moves">
            <h3>{t('prot.answers_to')}</h3>
            <LinkedRows rows={[data.answers]} onOpen={onOpen} />
          </section>
        ) : null}
        {data.replies.length ? (
          <section className="item-moves">
            <h3>{t('prot.answered_with')}</h3>
            <LinkedRows rows={data.replies} onOpen={onOpen} />
          </section>
        ) : null}

        <Files entry={e.id} files={data.files} canAdd={can.attach} onChange={reload} />

        <section className="item-moves">
          <div className="prot-history-head">
            <h3>{t('prot.history')}</h3>
            {can.handle && openState ? (
              <Button size="sm" variant="ghost" icon="note" onClick={() => setAsk('note')}>
                {t('prot.add_note')}
              </Button>
            ) : null}
          </div>
          <ol className="prot-history">
            {data.history.map((h) => (
              <li key={h.id} className={`prot-h prot-h-${h.kind}`}>
                <span className="prot-h-dot" aria-hidden="true" />
                <span className="prot-h-body">
                  <span className="strong">{historyTitle(h)}</span>
                  {h.text ? <span className="prot-h-text">{h.text}</span> : null}
                  {h.kind === 'corrected' && h.data?.changes ? (
                    <span className="muted small">
                      {Object.entries(h.data.changes as Record<string, { from: unknown; to: unknown }>)
                        .map(([k, v]) => t('prot.changed_field', { field: t(`prot.f_${k}` as MessageKey), from: show(v.from), to: show(v.to) }))
                        .join(' · ')}
                    </span>
                  ) : null}
                  <span className="muted small">{[dateTime(h.at), h.by_name].filter(Boolean).join(' · ')}</span>
                </span>
              </li>
            ))}
          </ol>
        </section>
      </div>

      {ask === 'assign' ? <AssignDialog entry={e} onClose={() => setAsk(null)} onDone={done} /> : null}
      {ask === 'note' || ask === 'close' || ask === 'reopen' || ask === 'void' ? <TextDialog kind={ask} entry={e} onClose={() => setAsk(null)} onDone={done} /> : null}
      {ask === 'correct' ? <CorrectDialog entry={e} onClose={() => setAsk(null)} onDone={done} /> : null}
    </Dialog>
  );
}

const show = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : typeof v === 'boolean' ? (v ? t('prot.yes') : t('prot.no')) : String(v));

function historyTitle(h: Detail['history'][number]) {
  switch (h.kind) {
    case 'assigned':
      return h.data?.due ? t('prot.h_assigned_due', { name: h.data?.to_name ?? '', day: day(h.data.due) }) : t('prot.h_assigned', { name: h.data?.to_name ?? '' });
    case 'answered':
      return t('prot.h_answered', { name: h.data?.name ?? '' });
    case 'file':
      return t('prot.h_file');
    case 'reopened':
      return h.data?.voided ? t('prot.h_reopened_void', { name: h.data.voided }) : t('prot.h_reopened');
    default:
      return t(`prot.h_${h.kind}` as MessageKey);
  }
}

function LinkedRows({ rows, onOpen }: { rows: Row[]; onOpen: (id: string) => void }) {
  return (
    <div className="list-card">
      {rows.map((r) => (
        <button key={r.id} className="list-row compact-move" onClick={() => onOpen(r.id)}>
          <span className="list-icon" aria-hidden="true">
            <Icon name={DIR_ICON[r.direction]} size={18} />
          </span>
          <span className="list-main">
            <span className="list-title">
              {r.name} · {subjectOf(r)}
            </span>
            <span className="list-sub">{[day(r.date), r.party].filter(Boolean).join(' · ')}</span>
          </span>
          <StateBadge r={r} />
        </button>
      ))}
    </div>
  );
}

const readBase64 = (f: File) =>
  new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(f);
  });

function Files({ entry, files, canAdd, onChange }: { entry: string; files: Detail['files']; canAdd: boolean; onChange: () => Promise<unknown> }) {
  const input = useRef<HTMLInputElement>(null);
  const { busy, run } = useSave();
  const [problem, setProblem] = useState<string | null>(null);
  const add = (list: FileList | null) => {
    const chosen = [...(list ?? [])];
    if (input.current) input.current.value = '';
    if (!chosen.length) return;
    const big = chosen.find((f) => f.size > MAX_FILE);
    if (big) return setProblem(t('prot.file_too_big', { name: big.name }));
    setProblem(null);
    void run(async () => {
      for (const f of chosen) await upload('protocol.attach', { id: entry, name: f.name, data: await readBase64(f) });
      await onChange();
    }, tn('prot.files_added', chosen.length));
  };
  const open = (id: string) =>
    void run(async () => {
      const f = await action<{ name: string; mime: string; data: string }>('protocol.file', { id });
      const bytes = Uint8Array.from(atob(f.data), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: f.mime }));
      const a = document.createElement('a');
      a.href = url;
      a.download = f.name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    });
  return (
    <section className="item-moves">
      <div className="prot-history-head">
        <h3>{t('prot.files')}</h3>
        {canAdd ? (
          <>
            <input ref={input} type="file" accept={ACCEPT} multiple hidden onChange={(e) => add(e.target.files)} />
            <Button size="sm" variant="ghost" icon="plus" onClick={() => input.current?.click()} busy={busy}>
              {t('prot.add_file')}
            </Button>
          </>
        ) : null}
      </div>
      {problem ? (
        <p className="form-error" role="alert">
          <Icon name="alert" size={14} /> {problem}
        </p>
      ) : null}
      {files.length ? (
        <div className="list-card">
          {files.map((f) => (
            <button key={f.id} className="list-row compact-move" onClick={() => open(f.id)}>
              <span className="list-icon" aria-hidden="true">
                <Icon name={f.mime === 'application/pdf' ? 'invoice' : 'image'} size={18} />
              </span>
              <span className="list-main">
                <span className="list-title">{f.name}</span>
                <span className="list-sub">{[size(f.size), dateTime(f.at), f.by_name].filter(Boolean).join(' · ')}</span>
              </span>
              <Icon name="download" size={18} />
            </button>
          ))}
        </div>
      ) : (
        <p className="muted">{canAdd ? t('prot.no_files_add') : t('prot.no_files')}</p>
      )}
    </section>
  );
}

const size = (n: number) => (n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1000))} KB`);

/* ---------------------------------------------------------------- Registering */

function RegisterDialog({ reply, manage, onClose, onDone }: { reply?: Detail['entry']; manage: boolean; onClose: () => void; onDone: (id: string) => Promise<void> }) {
  const { me } = useSession();
  const { toast } = useUi();
  const { busy, run } = useSave();
  const { data: people } = useAction<Person[]>('protocol.people');
  const { data: folders } = useAction<{ folder: string }[]>('protocol.folders');
  const [direction, setDirection] = useState<Direction>(reply ? 'out' : 'in');
  const [subject, setSubject] = useState(reply ? t('prot.answer_subject', { subject: reply.subject }) : '');
  const [party, setParty] = useState(reply?.party ?? '');
  const [channel, setChannel] = useState<Channel>(reply?.channel ?? 'post');
  const [theirRef, setTheirRef] = useState('');
  const [theirDate, setTheirDate] = useState('');
  const [pages, setPages] = useState('');
  const [folder, setFolder] = useState(reply?.folder ?? '');
  const [confidential, setConfidential] = useState(reply?.confidential ?? false);
  const [assignee, setAssignee] = useState('');
  const [due, setDue] = useState('');
  const [note, setNote] = useState('');
  const [chosen, setChosen] = useState<File[]>([]);
  const [tried, setTried] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const pagesN = pages.trim() ? Number(pages) : null;
  const errors = {
    subject: subject.trim() ? null : t('form.required'),
    party: party.trim() ? null : t('form.required'),
    pages: pagesN !== null && (!Number.isInteger(pagesN) || pagesN < 1) ? t('prot.pages_wrong') : null,
    files: chosen.some((f) => f.size > MAX_FILE) ? t('prot.file_too_big', { name: chosen.find((f) => f.size > MAX_FILE)!.name }) : null,
  };
  const save = () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    void run(async () => {
      const e = await action<{ id: string; name: string }>('protocol.register', {
        direction,
        subject: subject.trim(),
        party: party.trim(),
        channel,
        their_ref: direction === 'in' ? theirRef.trim() || null : null,
        their_date: direction === 'in' && theirDate ? theirDate : null,
        pages: pagesN,
        folder: folder.trim() || null,
        confidential,
        assignee: direction === 'in' && assignee ? assignee : null,
        due: direction === 'in' && due ? due : null,
        reply_to: reply?.id ?? null,
        note: note.trim() || null,
      });
      toast(t('prot.registered_as', { name: e.name }));
      for (const f of chosen) await upload('protocol.attach', { id: e.id, name: f.name, data: await readBase64(f) });
      await onDone(e.id);
    });
  };
  const handlers = manage ? (people ?? []) : (people ?? []).filter((p) => p.id === me?.userId);
  return (
    <Dialog
      title={reply ? t('prot.answer_title', { name: reply.name }) : t('prot.register')}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="book" onClick={save} busy={busy}>
            {t('prot.register_it')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        {!reply ? (
          <div className="segmented" role="radiogroup" aria-label={t('prot.direction')}>
            {DIRS.map((d) => (
              <button key={d.key} type="button" role="radio" aria-checked={direction === d.key} className={direction === d.key ? 'active' : ''} onClick={() => setDirection(d.key)}>
                <Icon name={d.icon} size={16} />
                {t(`prot.dir_${d.key}` as MessageKey)}
              </button>
            ))}
          </div>
        ) : (
          <p className="muted">{t('prot.answer_hint', { name: reply.name })}</p>
        )}
        <Field label={t('prot.subject')} hint={t('prot.subject_hint')} error={tried ? errors.subject : null} required>
          <input className="input" maxLength={300} value={subject} onChange={(e) => setSubject(e.target.value)} data-autofocus />
        </Field>
        <div className="form-grid">
          <Field label={direction === 'out' ? t('prot.to') : direction === 'internal' ? t('prot.office') : t('prot.from')} error={tried ? errors.party : null} required>
            <input className="input" maxLength={200} value={party} onChange={(e) => setParty(e.target.value)} />
          </Field>
          <Field label={direction === 'out' ? t('prot.sent_by') : t('prot.channel')}>
            <select className="input" value={channel} onChange={(e) => setChannel(e.target.value as Channel)}>
              {CHANNELS.map((c) => (
                <option key={c} value={c}>
                  {t(`prot.ch_${c}` as MessageKey)}
                </option>
              ))}
            </select>
          </Field>
          {direction === 'in' ? (
            <>
              <Field label={t('prot.their_ref')} hint={t('prot.their_ref_hint')}>
                <input className="input" maxLength={60} value={theirRef} onChange={(e) => setTheirRef(e.target.value)} />
              </Field>
              <Field label={t('prot.their_date')}>
                <input className="input" type="date" value={theirDate} onChange={(e) => setTheirDate(e.target.value)} />
              </Field>
            </>
          ) : null}
          <Field label={t('prot.pages')} error={tried ? errors.pages : null}>
            <input className="input tabular" inputMode="numeric" value={pages} onChange={(e) => setPages(e.target.value)} />
          </Field>
          <Field label={t('prot.folder')} hint={t('prot.folder_hint')}>
            <input className="input" maxLength={120} list="prot-folders" value={folder} onChange={(e) => setFolder(e.target.value)} />
            <datalist id="prot-folders">
              {(folders ?? []).map((f) => (
                <option key={f.folder} value={f.folder} />
              ))}
            </datalist>
          </Field>
        </div>
        {direction === 'in' ? (
          <div className="form-grid">
            <Field label={t('prot.handled_by')} hint={manage ? t('prot.handled_by_hint') : t('prot.handled_by_self')}>
              <select className="input" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
                <option value="">{t('prot.nobody_yet')}</option>
                {handlers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('prot.due')} hint={t('prot.due_hint')}>
              <input className="input" type="date" value={due} onChange={(e) => setDue(e.target.value)} />
            </Field>
          </div>
        ) : null}
        <label className="look-toggle prot-confidential">
          <span>
            <span className="strong">{t('prot.confidential')}</span>
            <span className="muted small">{t('prot.confidential_hint')}</span>
          </span>
          <Switch checked={confidential} onChange={setConfidential} label={t('prot.confidential')} />
        </label>
        <Field label={t('prot.note')} hint={t('prot.note_hint')}>
          <input className="input" maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <div className="prot-attach">
          <input ref={fileInput} type="file" accept={ACCEPT} multiple hidden onChange={(e) => setChosen([...chosen, ...(e.target.files ?? [])])} />
          <Button size="sm" icon="plus" onClick={() => fileInput.current?.click()}>
            {t('prot.add_scan')}
          </Button>
          <span className="muted small">{chosen.length ? chosen.map((f) => `${f.name} (${size(f.size)})`).join(', ') : t('prot.scan_hint')}</span>
          {chosen.length ? (
            <button type="button" className="link-button" onClick={() => setChosen([])}>
              {t('prot.remove_files')}
            </button>
          ) : null}
        </div>
        {tried && errors.files ? (
          <p className="form-error" role="alert">
            <Icon name="alert" size={14} /> {errors.files}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

function AssignDialog({ entry, onClose, onDone }: { entry: Detail['entry']; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const { data: people } = useAction<Person[]>('protocol.people');
  const [who, setWho] = useState(entry.assignee ?? '');
  const [due, setDue] = useState(entry.due ?? '');
  const [note, setNote] = useState('');
  const save = () =>
    void run(async () => {
      await action('protocol.assign', { id: entry.id, assignee: who, due: due || null, note: note.trim() || null });
      await onDone();
    }, t('prot.given_done'));
  return (
    <Dialog
      title={t('prot.give_title', { name: entry.name })}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="users" onClick={save} busy={busy} disabled={!who}>
            {t('prot.give')}
          </Button>
        </>
      }
    >
      <Field label={t('prot.handled_by')}>
        <select className="input" value={who} onChange={(e) => setWho(e.target.value)} data-autofocus>
          <option value="" disabled>
            {t('prot.pick_person')}
          </option>
          {(people ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label={t('prot.due')} hint={t('prot.due_hint')}>
        <input className="input" type="date" value={due} min={entry.date} onChange={(e) => setDue(e.target.value)} />
      </Field>
      <Field label={t('prot.instructions')} hint={t('prot.instructions_hint')}>
        <textarea className="input" rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
    </Dialog>
  );
}

const TEXT_ACTION = {
  note: { action: 'protocol.note', field: 'text', icon: 'note', danger: false },
  close: { action: 'protocol.close', field: 'outcome', icon: 'check', danger: false },
  reopen: { action: 'protocol.reopen', field: 'reason', icon: 'undo', danger: false },
  void: { action: 'protocol.void', field: 'reason', icon: 'x', danger: true },
} as const;

function TextDialog({ kind, entry, onClose, onDone }: { kind: keyof typeof TEXT_ACTION; entry: Detail['entry']; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [text, setText] = useState('');
  const a = TEXT_ACTION[kind];
  const save = () =>
    void run(async () => {
      await action(a.action, { id: entry.id, [a.field]: text.trim() });
      await onDone();
    }, t(`prot.${kind}_done` as MessageKey));
  return (
    <Dialog
      title={t(`prot.${kind}_title` as MessageKey, { name: entry.name })}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant={a.danger ? 'danger' : 'primary'} icon={a.icon} onClick={save} busy={busy} disabled={text.trim().length < (kind === 'note' ? 1 : 3)}>
            {t(`prot.${kind}_button` as MessageKey)}
          </Button>
        </>
      }
    >
      <p className="muted">{t(`prot.${kind}_body` as MessageKey)}</p>
      <Field label={t(`prot.${kind}_label` as MessageKey)}>
        <textarea className="input" rows={3} maxLength={kind === 'note' ? 2000 : 1000} value={text} onChange={(e) => setText(e.target.value)} data-autofocus />
      </Field>
    </Dialog>
  );
}

function CorrectDialog({ entry, onClose, onDone }: { entry: Detail['entry']; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [v, setV] = useState({
    subject: entry.subject,
    party: entry.party,
    channel: entry.channel,
    their_ref: entry.their_ref ?? '',
    their_date: entry.their_date ?? '',
    pages: entry.pages ? String(entry.pages) : '',
    folder: entry.folder ?? '',
    confidential: entry.confidential,
  });
  const [reason, setReason] = useState('');
  const put = (k: keyof typeof v) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value });
  const pagesN = v.pages.trim() ? Number(v.pages) : null;
  const ok = v.subject.trim() && v.party.trim() && reason.trim().length >= 3 && (pagesN === null || (Number.isInteger(pagesN) && pagesN > 0));
  const save = () =>
    void run(async () => {
      await action('protocol.correct', {
        id: entry.id,
        subject: v.subject.trim(),
        party: v.party.trim(),
        channel: v.channel,
        their_ref: v.their_ref.trim() || null,
        their_date: v.their_date || null,
        pages: pagesN,
        folder: v.folder.trim() || null,
        confidential: v.confidential,
        reason: reason.trim(),
      });
      await onDone();
    }, t('prot.corrected_done'));
  return (
    <Dialog
      title={t('prot.correct_title', { name: entry.name })}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" onClick={save} busy={busy} disabled={!ok}>
            {t('prot.correct')}
          </Button>
        </>
      }
    >
      <p className="muted">{t('prot.correct_body')}</p>
      <Field label={t('prot.subject')}>
        <input className="input" maxLength={300} value={v.subject} onChange={put('subject')} />
      </Field>
      <div className="form-grid">
        <Field label={entry.direction === 'out' ? t('prot.to') : t('prot.from')}>
          <input className="input" maxLength={200} value={v.party} onChange={put('party')} />
        </Field>
        <Field label={t('prot.channel')}>
          <select className="input" value={v.channel} onChange={put('channel')}>
            {CHANNELS.map((c) => (
              <option key={c} value={c}>
                {t(`prot.ch_${c}` as MessageKey)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('prot.their_ref')}>
          <input className="input" maxLength={60} value={v.their_ref} onChange={put('their_ref')} />
        </Field>
        <Field label={t('prot.their_date')}>
          <input className="input" type="date" value={v.their_date} onChange={put('their_date')} />
        </Field>
        <Field label={t('prot.pages')}>
          <input className="input tabular" inputMode="numeric" value={v.pages} onChange={put('pages')} />
        </Field>
        <Field label={t('prot.folder')}>
          <input className="input" maxLength={120} value={v.folder} onChange={put('folder')} />
        </Field>
      </div>
      <label className="look-toggle prot-confidential">
        <span>
          <span className="strong">{t('prot.confidential')}</span>
          <span className="muted small">{t('prot.confidential_hint')}</span>
        </span>
        <Switch checked={v.confidential} onChange={(c) => setV({ ...v, confidential: c })} label={t('prot.confidential')} />
      </label>
      <Field label={t('prot.correct_reason')} hint={t('prot.correct_reason_hint')} required>
        <input className="input" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- The register book */

function BookDialog({ years, onClose }: { years: number[]; onClose: () => void }) {
  const { tenant } = useSession();
  const [year, setYear] = useState(years[0]!);
  const { data: rows } = useAction<Row[]>('protocol.list', { year, book: true, limit: 2000 });
  const [printing, setPrinting] = useState(false);
  const print = () => {
    setPrinting(true);
    setTimeout(() => {
      window.print();
      setPrinting(false);
    }, 50);
  };
  const csv = () =>
    downloadCsv(
      `${t('prot.book_file')}-${year}.csv`,
      [t('prot.c_no'), t('prot.c_date'), t('prot.direction'), t('prot.c_subject'), t('prot.from_to'), t('prot.their_ref'), t('prot.folder'), t('prot.c_handler'), t('prot.c_state')],
      (rows ?? []).map((r) => [r.name, r.date, t(`prot.dir_${r.direction}` as MessageKey), subjectOf(r), r.party ?? '', r.their_ref ?? '', r.folder ?? '', r.assignee_name ?? '', stateLabel(r.state)]),
    );
  const table = (
    <table className="prot-book-table">
      <thead>
        <tr>
          <th>{t('prot.c_no')}</th>
          <th>{t('prot.c_date')}</th>
          <th>{t('prot.b_dir')}</th>
          <th>{t('prot.c_subject')}</th>
          <th>{t('prot.from_to')}</th>
          <th>{t('prot.their_ref')}</th>
          <th>{t('prot.folder')}</th>
          <th>{t('prot.c_handler')}</th>
          <th>{t('prot.c_state')}</th>
        </tr>
      </thead>
      <tbody>
        {(rows ?? []).map((r) => (
          <tr key={r.id} className={r.state === 'void' ? 'void' : ''}>
            <td className="tabular">{r.name}</td>
            <td className="tabular">{day(r.date)}</td>
            <td>{t(`prot.b_${r.direction}` as MessageKey)}</td>
            <td>{subjectOf(r)}</td>
            <td>{r.party ?? ''}</td>
            <td>{r.their_ref ?? ''}</td>
            <td>{r.folder ?? ''}</td>
            <td>{r.assignee_name ?? ''}</td>
            <td>{stateLabel(r.state)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
  return (
    <Dialog
      title={t('prot.book_title', { year })}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button icon="download" onClick={csv} disabled={!rows?.length}>
            {t('prot.spreadsheet')}
          </Button>
          <Button variant="primary" icon="printer" onClick={print} disabled={!rows?.length}>
            {t('prot.print')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <div className="prot-book-head">
          <p className="muted">{t('prot.book_body')}</p>
          {years.length > 1 ? (
            <select className="input" value={year} onChange={(e) => setYear(Number(e.target.value))} aria-label={t('prot.year')}>
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          ) : null}
        </div>
        {!rows ? <Skeleton h={200} /> : !rows.length ? <EmptyState icon="book" title={t('prot.book_empty', { year })} /> : <div className="prot-book-wrap">{table}</div>}
      </div>
      {printing
        ? createPortal(
            <div className="print-sheet print-book">
              <div className="prot-book-paper">
                <header>
                  <strong>{tenant?.name}</strong>
                  <h1>{t('prot.book_title', { year })}</h1>
                  <span>{tn('prot.n_entries', rows?.length ?? 0)}</span>
                </header>
                {table}
              </div>
            </div>,
            document.body,
          )
        : null}
    </Dialog>
  );
}
